import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { admin, cleanApiBaseUrl, records, rag } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { FileText, Upload, Globe, Database, TrendingUp, Clock, CheckCircle, AlertCircle, Cpu, Sparkles } from 'lucide-react';
import { format } from 'date-fns';

function StatCard({ label, value, sub, icon: Icon, color }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5 flex items-start gap-4">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${color}`}>
        <Icon size={18} />
      </div>
      <div>
        <div className="text-2xl font-semibold text-gray-900">{value ?? '—'}</div>
        <div className="text-sm text-gray-500 mt-0.5">{label}</div>
        {sub && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    published: 'bg-green-50 text-green-700',
    draft: 'bg-gray-100 text-gray-600',
    archived: 'bg-amber-50 text-amber-700',
  };
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${map[status] || 'bg-gray-100 text-gray-600'}`}>{status}</span>;
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [ragStatus, setRagStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cityName, setCityName] = useState('');

  useEffect(() => {
    Promise.all([
      user?.role === 'admin' ? admin.stats() : Promise.resolve(null),
      records.list({ limit: 8, page: 1 }),
      rag.status().catch(() => null)
    ]).then(([s, r, rs]) => {
      if (s) setStats(s.data);
      setRecent(r.data.records || []);
      if (rs) setRagStatus(rs.data);
    }).finally(() => setLoading(false));

    // Fetch city config for branding
    const apiBase = cleanApiBaseUrl();
    fetch(`${apiBase}/api/config`)
      .then(r => r.json())
      .then(data => { if (data.city_name) setCityName(data.city_name); })
      .catch(() => {});
  }, [user]);

  const fmtSize = (bytes) => {
    if (!bytes) return '0 B';
    const gb = bytes / 1e9;
    if (gb >= 1) return `${gb.toFixed(1)} GB`;
    const mb = bytes / 1e6;
    if (mb >= 1) return `${mb.toFixed(1)} MB`;
    return `${(bytes / 1e3).toFixed(0)} KB`;
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-gray-900">Welcome back, {user?.name?.split(' ')[0]}</h1>
        <p className="text-sm text-gray-500 mt-1">CogniVault · {cityName && cityName !== 'Generic' ? `${cityName} ` : ''}Digital Records Repository</p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <StatCard label="Total records" value={stats.total_records?.toLocaleString()} sub={fmtSize(stats.total_size_bytes)} icon={FileText} color="bg-blue-50 text-blue-600" />
          <StatCard label="Published public" value={stats.published_records?.toLocaleString()} sub="Visible on public portal" icon={Globe} color="bg-teal-50 text-teal-600" />
          <StatCard label="Migrations run" value={stats.completed_migrations} sub="Alchemy imports" icon={Database} color="bg-purple-50 text-purple-600" />
          <StatCard label="Staff users" value={stats.total_users} sub="Active accounts" icon={TrendingUp} color="bg-amber-50 text-amber-600" />

          {/* AI Status */}
          <div className="bg-white rounded-xl border border-gray-100 p-5 flex items-start gap-4">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${ragStatus?.ollama?.ok ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-500'}`}>
              <Cpu size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${ragStatus?.ollama?.ok ? 'bg-green-500' : 'bg-red-500'}`} />
                <span className="text-sm font-semibold text-gray-900">{ragStatus?.ollama?.ok ? 'AI Online' : 'AI Offline'}</span>
              </div>
              <div className="text-xs text-gray-400 mt-1">On-premises LLM</div>
              {ragStatus?.ollama?.ok && ragStatus?.ollama?.embed_model && ragStatus?.ollama?.chat_model && (
                <div className="flex flex-wrap gap-1 mt-2">
                  <span className="text-xs bg-gray-50 text-gray-500 px-1.5 py-0.5 rounded border border-gray-100">{ragStatus.ollama.embed_model.split(':')[0]}</span>
                  <span className="text-xs bg-gray-50 text-gray-500 px-1.5 py-0.5 rounded border border-gray-100">{ragStatus.ollama.chat_model.split(':')[0]}</span>
                </div>
              )}
            </div>
          </div>

          {/* RAG Indexing */}
          <Link to="/ask" className="bg-white rounded-xl border border-gray-100 p-5 flex items-start gap-4 hover:border-purple-200 transition-colors">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-purple-50 text-purple-600">
              <Sparkles size={18} />
            </div>
            <div className="flex-1">
              <div className="text-2xl font-semibold text-gray-900">{ragStatus?.indexed_records ?? '—'}<span className="text-sm font-normal text-gray-400"> / {ragStatus?.indexable_records ?? '—'}</span></div>
              <div className="text-sm text-gray-500 mt-0.5">Records indexed for AI</div>
              {ragStatus?.indexable_records > 0 && (
                <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-purple-500 rounded-full transition-all duration-500" style={{ width: `${Math.round((ragStatus.indexed_records / ragStatus.indexable_records) * 100)}%` }} />
                </div>
              )}
            </div>
          </Link>
        </div>
      )}

      {/* By type chart */}
      {stats?.by_type?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-5 mb-6">
          <h2 className="text-sm font-medium text-gray-700 mb-4">Records by type</h2>
          <div className="space-y-2.5">
            {stats.by_type.map(({ document_type, count }) => {
              const pct = Math.round((count / stats.total_records) * 100) || 1;
              return (
                <div key={document_type} className="flex items-center gap-3">
                  <div className="text-xs text-gray-500 w-32 truncate">{document_type}</div>
                  <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-blue-500 to-teal-400 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="text-xs text-gray-500 w-10 text-right">{parseInt(count).toLocaleString()}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Quick actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { to: '/upload', icon: Upload, label: 'Upload record', color: 'bg-blue-600 text-white hover:bg-blue-700' },
          { to: '/search', icon: FileText, label: 'Search records', color: 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50' },
          { to: '/migration', icon: Database, label: 'Run migration', color: 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50' },
          { to: '/public', icon: Globe, label: 'Public portal', color: 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50', external: true },
        ].map(({ to, icon: Icon, label, color, external }) => (
          external
            ? <a key={to} href={to} target="_blank" rel="noopener noreferrer" className={`flex items-center gap-2.5 px-4 py-3 rounded-xl text-sm font-medium transition-all ${color}`}><Icon size={15} />{label}</a>
            : <Link key={to} to={to} className={`flex items-center gap-2.5 px-4 py-3 rounded-xl text-sm font-medium transition-all ${color}`}><Icon size={15} />{label}</Link>
        ))}
      </div>

      {/* Recent records */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-50">
          <h2 className="text-sm font-medium text-gray-700">Recent records</h2>
          <Link to="/records" className="text-xs text-blue-600 hover:text-blue-700">View all →</Link>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-gray-400">Loading...</div>
        ) : recent.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-400">No records yet. <Link to="/upload" className="text-blue-600">Upload one →</Link></div>
        ) : (
          <table className="w-full">
            <thead><tr className="border-b border-gray-50">
              {['Title','Type','Department','Status','Date'].map(h => (
                <th key={h} className="px-5 py-3 text-left text-xs font-medium text-gray-400">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {recent.map(r => (
                <tr key={r.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition-colors">
                  <td className="px-5 py-3 text-sm text-gray-900 max-w-xs truncate font-medium">{r.title}</td>
                  <td className="px-5 py-3 text-sm text-gray-500">{r.document_type}</td>
                  <td className="px-5 py-3 text-sm text-gray-500">{r.department || '—'}</td>
                  <td className="px-5 py-3"><StatusBadge status={r.status} /></td>
                  <td className="px-5 py-3 text-xs text-gray-400">{r.created_at ? format(new Date(r.created_at), 'MMM d, yyyy') : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
